import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMateriaalProjects, fetchMateriaalProjectStages, updateMateriaalProjectStage } from '@/api/materiaal-projects';
import type { MateriaalProject, MateriaalProjectStage } from '@/api/materiaal-projects';
import { getStageClasses } from '@/utils/stages';

function OpgelijstBadge({ done, total, size = 'sm' }: { done: number; total: number; size?: 'sm' | 'xs' }) {
  if (total === 0) return <span className="text-gray-400">&ndash;</span>;

  const badgeClass =
    done === total
      ? 'bg-green-100 text-green-700'
      : done > 0
        ? 'bg-amber-100 text-amber-700'
        : 'bg-gray-100 text-gray-600';

  const sizeClass = size === 'sm'
    ? 'text-xs px-2.5 py-0.5'
    : 'text-[11px] px-2 py-0.5';

  return (
    <span className={`inline-block font-semibold rounded-full ${badgeClass} ${sizeClass}`}>
      {done}/{total} opgelijst
    </span>
  );
}

const dateFormatter = new Intl.DateTimeFormat('nl-BE', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function formatDate(dateStr: string | false): string {
  if (!dateStr) return '';
  // Odoo date_order can be "2026-01-28" or "2026-01-28 10:30:00"
  const dateOnly = dateStr.slice(0, 10);
  const d = new Date(dateOnly + 'T12:00:00');
  if (isNaN(d.getTime())) return '';
  return dateFormatter.format(d);
}

export function MateriaalProjectenView() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<MateriaalProject[]>([]);
  const [stages, setStages] = useState<MateriaalProjectStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.allSettled([fetchMateriaalProjects(), fetchMateriaalProjectStages()])
      .then(([projectsResult, stagesResult]) => {
        if (cancelled) return;
        if (projectsResult.status === 'fulfilled') {
          setProjects(projectsResult.value);
        } else {
          setError(projectsResult.reason instanceof Error ? projectsResult.reason.message : 'Kon projecten niet laden');
          setProjects([]);
        }
        if (stagesResult.status === 'fulfilled') {
          setStages(stagesResult.value);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleStageChange = useCallback(async (projectId: number, newStageId: number) => {
    // Optimistic update
    setProjects((prev) =>
      prev.map((p) =>
        p.projectId === projectId
          ? { ...p, stageId: newStageId, stageName: stages.find((s) => s.id === newStageId)?.name ?? p.stageName }
          : p,
      ),
    );
    try {
      await updateMateriaalProjectStage(projectId, newStageId);
    } catch {
      // Revert on failure — refetch to get true state
      try {
        const fresh = await fetchMateriaalProjects();
        setProjects(fresh);
      } catch {
        // Ignore secondary fetch error
      }
    }
  }, [stages]);

  // Client-side search filter
  const filtered = useMemo(() => {
    if (!searchInput.trim()) return projects;
    const q = searchInput.toLowerCase();
    return projects.filter(
      (p) =>
        p.projectName.toLowerCase().includes(q) ||
        p.clientName.toLowerCase().includes(q) ||
        p.soName.toLowerCase().includes(q),
    );
  }, [projects, searchInput]);

  const handleProjectClick = (project: MateriaalProject) => {
    if (project.projectId === null) return;
    navigate(`/project/${project.projectId}`);
  };

  return (
    <div>
      {/* Header */}
      <div className="px-4 lg:px-8 pt-4 lg:pt-6 pb-2 lg:pb-4">
        <h1 className="text-lg lg:text-xl font-extrabold text-td-navy text-center">
          Materiaal Projecten
        </h1>
        <p className="text-td-muted text-xs lg:text-sm mt-1 text-center">
          {!loading && !error && `${filtered.length} projecten`}
        </p>
      </div>

      {/* Search */}
      <div className="px-4 lg:px-8 pb-3">
        <div className="max-w-2xl mx-auto relative">
          <svg
            className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <label htmlFor="materiaal-search" className="sr-only">
            Zoek project, klant of bestelling
          </label>
          <input
            id="materiaal-search"
            type="text"
            placeholder="Zoek project, klant of bestelling..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue focus:border-transparent"
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 lg:px-8 pb-8 mt-3 lg:mt-5">
        {/* Loading state */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
              <span className="sr-only">Laden...</span>
            </div>
            <p className="text-td-muted text-sm mt-3">Projecten laden...</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
            <p className="font-semibold">Fout bij laden</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Data */}
        {!loading && !error && (
          <>
            {/* Empty state */}
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <p className="text-td-muted text-sm">
                  Geen materiaal projecten gevonden
                  {searchInput && (
                    <span>
                      {' '}
                      voor &quot;{searchInput}&quot;
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Desktop table */}
            {filtered.length > 0 && (
              <div className="hidden lg:block">
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-td-muted uppercase tracking-wider">Project</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-td-muted uppercase tracking-wider">Klant</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-td-muted uppercase tracking-wider">Verkooporder</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-td-muted uppercase tracking-wider">Datum</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-td-muted uppercase tracking-wider">Opgelijst</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-td-muted uppercase tracking-wider">Fase</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((project) => {
                        const isLinked = project.projectId !== null;
                        return (
                          <tr
                            key={project.soId}
                            onClick={isLinked ? () => handleProjectClick(project) : undefined}
                            className={
                              isLinked
                                ? 'border-b border-gray-50 hover:bg-td-blue/5 transition-colors cursor-pointer'
                                : 'border-b border-gray-50'
                            }
                          >
                            <td className={`px-4 py-3 text-sm font-semibold ${isLinked ? 'text-td-navy' : 'text-gray-400'}`}>
                              {project.projectName}
                              {!isLinked && (
                                <span className="ml-2 inline-block text-xs font-normal text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                                  Nog geen project
                                </span>
                              )}
                            </td>
                            <td className={`px-4 py-3 text-sm ${isLinked ? 'text-gray-600' : 'text-gray-400'}`}>{project.clientName}</td>
                            <td className={`px-4 py-3 text-sm ${isLinked ? 'text-gray-600' : 'text-gray-400'}`}>{project.soName}</td>
                            <td className={`px-4 py-3 text-sm ${isLinked ? 'text-gray-500' : 'text-gray-400'}`}>{formatDate(project.dateOrder)}</td>
                            <td className="px-4 py-3 text-sm">
                              <OpgelijstBadge done={project.opgelijstDone} total={project.opgelijstTotal} />
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {isLinked && project.stageId !== null ? (
                                <label className="sr-only" htmlFor={`stage-desktop-${project.soId}`}>
                                  Fase voor {project.projectName}
                                </label>
                              ) : null}
                              {isLinked && project.stageId !== null ? (
                                <select
                                  id={`stage-desktop-${project.soId}`}
                                  value={project.stageId}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleStageChange(project.projectId!, Number(e.target.value));
                                  }}
                                  className={`text-sm font-semibold border-0 rounded-full px-3 py-1 focus:outline-none focus:ring-2 focus:ring-td-blue focus:ring-offset-1 cursor-pointer ${getStageClasses(project.stageName ?? '')}`}
                                >
                                  {stages.map((s) => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                  ))}
                                </select>
                              ) : (
                                <span className="text-gray-400">&ndash;</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Mobile cards */}
            {filtered.length > 0 && (
              <div className="lg:hidden space-y-3 pb-20">
                {filtered.map((project) => {
                  const isLinked = project.projectId !== null;
                  const cardContent = (
                    <>
                      <p className={`text-sm font-semibold ${isLinked ? 'text-td-navy' : 'text-gray-400'}`}>
                        {project.projectName}
                        {!isLinked && (
                          <span className="ml-2 inline-block text-xs font-normal text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                            Nog geen project
                          </span>
                        )}
                      </p>
                      <p className={`text-sm mt-0.5 ${isLinked ? 'text-gray-600' : 'text-gray-400'}`}>{project.clientName}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className={`text-xs ${isLinked ? 'text-gray-500' : 'text-gray-400'}`}>{project.soName}</p>
                        <OpgelijstBadge done={project.opgelijstDone} total={project.opgelijstTotal} size="xs" />
                        <p className="text-xs text-gray-400">{formatDate(project.dateOrder)}</p>
                      </div>
                      <div className="mt-1">
                        {isLinked && project.stageId !== null ? (
                          <>
                            <label className="sr-only" htmlFor={`stage-mobile-${project.soId}`}>
                              Fase voor {project.projectName}
                            </label>
                            <select
                              id={`stage-mobile-${project.soId}`}
                              value={project.stageId}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation();
                                handleStageChange(project.projectId!, Number(e.target.value));
                              }}
                              className={`text-xs font-semibold border-0 rounded-full px-3 py-1 focus:outline-none focus:ring-2 focus:ring-td-blue focus:ring-offset-1 cursor-pointer ${getStageClasses(project.stageName ?? '')}`}
                            >
                              {stages.map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                            </select>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">&ndash;</span>
                        )}
                      </div>
                    </>
                  );

                  return isLinked ? (
                    <button
                      key={project.soId}
                      type="button"
                      onClick={() => handleProjectClick(project)}
                      className="w-full text-left bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow cursor-pointer"
                    >
                      {cardContent}
                    </button>
                  ) : (
                    <div
                      key={project.soId}
                      className="w-full text-left bg-white rounded-lg shadow-sm border border-gray-100 p-4"
                    >
                      {cardContent}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
